import { Text } from "@redotech/redo-web/text";

import { memo, useState } from "react";

import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import FlexBottomIcon from "@redotech/redo-icons/flex-align-bottom.svg";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-01.svg";
import ToggleIcon from "@redotech/redo-icons/toggle-03-left_filled.svg";
import { WidgetElementType } from "@redotech/redo-storefront-common/widget-config";
import { WidgetConfig } from "@redotech/redo-storefront-widget-common/widget";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { useNavigate } from "react-router-dom";
import { useMerchantAppLogin } from "../../../../merchant-app";
import * as styles from "../general.module.css";

export const LegacyWidgetsConfig = memo(function LegacyWidgetsConfig({
  teamId,
  widgets,
}: {
  teamId: string;
  widgets: WidgetConfig[];
}) {
  const navigate = useNavigate();
  const merchantAppLogin = useMerchantAppLogin();
  const [pendingPath, setPendingPath] = useState<string>();

  return (
    <>
      {widgets.map((widget, index) => (
        <WidgetItem
          index={index}
          key={widget.name}
          onOpen={setPendingPath}
          widget={widget}
        />
      ))}
      <ToggleWidgetItem onOpen={setPendingPath} />
      <PDPWidgetItem onOpen={setPendingPath} />
      <AddWidgetButton onOpen={setPendingPath} />
      <RedoModal
        isOpen={pendingPath !== undefined}
        onModalCloseRequested={() => setPendingPath(undefined)}
        primaryButton={{
          text: "Go To Coverage Builder Instead",
          onClick: () => {
            setPendingPath(undefined);
            merchantAppLogin(teamId, "/settings/coverage/coverage-builder");
          },
        }}
        secondaryButton={{
          text: "Continue to legacy",
          onClick: () => {
            if (pendingPath !== undefined) {
              navigate(pendingPath);
            }
          },
        }}
        title={
          <Text as="span" fontSize="lg" fontWeight="semibold" textColor="error">
            Legacy Widgets are about to be removed
          </Text>
        }
      >
        <Text fontSize="sm" textColor="error">
          Please contact the Coverage Team immediately if there is something
          here you need
        </Text>
      </RedoModal>
    </>
  );
});

const WidgetItem = memo(function WidgetItem({
  widget,
  index,
  onOpen,
}: {
  widget: WidgetConfig;
  index: number;
  onOpen: (path: string) => void;
}) {
  let widgetIcon = InfoCircleIcon;
  switch (widget.elementType) {
    case WidgetElementType.INFO_CARD:
      widgetIcon = InfoCircleIcon;
      break;
    case WidgetElementType.BOTH_CHECKOUT_BUTTONS:
      widgetIcon = ShoppingCartIcon;
      break;
    case WidgetElementType.COVERAGE_CHECKOUT_BUTTON:
      widgetIcon = ShoppingCartIcon;
      break;
    case WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON:
      widgetIcon = ShoppingCartIcon;
      break;
    case WidgetElementType.SINGLE_TOGGLE:
      widgetIcon = InfoCircleIcon;
      break;
    case WidgetElementType.CART_CHECKBOX:
      widgetIcon = InfoCircleIcon;
      break;
    case WidgetElementType.DOUBLE_TOGGLE:
      widgetIcon = InfoCircleIcon;
      break;
    case WidgetElementType.CART_CARD:
      widgetIcon = InfoCircleIcon;
      break;
    case WidgetElementType.CHECKOUT_BUTTON:
      widgetIcon = ShoppingCartIcon;
      break;
  }
  return (
    <a
      className={styles.widgetSubsectionItem}
      onClick={() => {
        onOpen("../widgets?index=" + index);
      }}
    >
      <div className={styles.widgetSubsectionItemContent}>
        <RedoIcon color="secondary" Icon={widgetIcon} size={20} />
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          {widget.name}
        </Text>
      </div>
      <RedoIcon color="tertiary" Icon={ChevronRightIcon} size={16} />
    </a>
  );
});

const ToggleWidgetItem = memo(function ToggleWidgetItem({
  onOpen,
}: {
  onOpen: (path: string) => void;
}) {
  return (
    <a
      className={styles.widgetSubsectionItem}
      onClick={() => {
        onOpen("../toggle");
      }}
    >
      <div className={styles.widgetSubsectionItemContent}>
        <RedoIcon color="secondary" Icon={ToggleIcon} size={20} />
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          Toggle
        </Text>
      </div>
      <RedoIcon color="tertiary" Icon={ChevronRightIcon} size={16} />
    </a>
  );
});

const PDPWidgetItem = memo(function PDPWidgetItem({
  onOpen,
}: {
  onOpen: (path: string) => void;
}) {
  return (
    <a
      className={styles.widgetSubsectionItem}
      onClick={() => {
        onOpen("../pdp");
      }}
    >
      <div className={styles.widgetSubsectionItemContent}>
        <RedoIcon color="secondary" Icon={FlexBottomIcon} size={20} />
        <Text fontSize="sm" fontWeight="medium" textColor="primary">
          Product Detail Page
        </Text>
      </div>
      <RedoIcon color="tertiary" Icon={ChevronRightIcon} size={16} />
    </a>
  );
});

const AddWidgetButton = memo(function AddWidgetButton({
  onOpen,
}: {
  onOpen: (path: string) => void;
}) {
  return (
    <a
      onClick={() => {
        onOpen("../widgets");
      }}
    >
      <button className={styles.addWidgetButton} type="button">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          + Add widget
        </Text>
      </button>
    </a>
  );
});
